// Thin client for our FastAPI proxy (WooCommerce + Tutor LMS).
// All product/course/order data goes through the backend; consumer keys
// never reach the browser.
import { safeReadJson, API } from "@/lib/api";

const LANG_KEY = "geovinum.lang.v1";

function activeLang() {
  if (typeof window === "undefined") return "";
  try {
    const url = new URLSearchParams(window.location.search).get("lang");
    if (url) return url;
    return localStorage.getItem(LANG_KEY) || "";
  } catch { return ""; }
}

async function jget(path) {
  const r = await fetch(`${API}${path}`, { headers: { Accept: "application/json" } });
  if (!r.ok) throw new Error(`API ${r.status}`);
  const data = await safeReadJson(r);
  if (data?._empty || data?._raw) throw new Error(`API ${r.status}: non-JSON body`);
  return data;
}

// Decode common HTML entities returned by WP (e.g. &#038; → &).
export function decodeHtml(s) {
  if (!s) return "";
  if (typeof document === "undefined") return s;
  const ta = document.createElement("textarea");
  ta.innerHTML = s;
  return ta.value;
}

export function fetchWooProducts({ search = "", perPage = 12, page = 1, category = "", lang } = {}) {
  const qs = new URLSearchParams({ per_page: String(perPage), page: String(page) });
  if (search) qs.set("search", search);
  if (category) qs.set("category", category);
  const l = lang ?? activeLang();
  if (l) qs.set("lang", l);
  return jget(`/api/woo/products?${qs.toString()}`);
}

export function fetchWooOrders({ email }) {
  if (!email) return Promise.resolve([]);
  const qs = new URLSearchParams({ customer_email: email });
  return jget(`/api/woo/orders?${qs.toString()}`);
}

export function fetchTutorCourses({ search = "", perPage = 12, page = 1, lang } = {}) {
  const qs = new URLSearchParams({ per_page: String(perPage), page: String(page) });
  if (search) qs.set("search", search);
  const l = lang ?? activeLang();
  if (l) qs.set("lang", l);
  return jget(`/api/tutor/courses?${qs.toString()}`);
}

export function searchAll(q, limit = 6, lang) {
  const qs = new URLSearchParams({ q, limit: String(limit) });
  const l = lang ?? activeLang();
  if (l) qs.set("lang", l);
  return jget(`/api/search/all?${qs.toString()}`);
}

// Tutor LMS enrolment — requires a JWT token (Authorization: Bearer …).
const AUTH_KEY = "geovinum.auth.v1";
function authHeaders() {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    const t = parsed?.token;
    return t ? { Authorization: `Bearer ${t}` } : {};
  } catch { return {}; }
}

export async function enrollInCourse(courseId) {
  const r = await fetch(`${API}/api/tutor/enroll`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json", ...authHeaders() },
    body: JSON.stringify({ course_id: Number(courseId) }),
  });
  const data = await safeReadJson(r);
  if (data?._empty || data?._raw) {
    const err = new Error(`Enrolment failed (HTTP ${r.status}: non-JSON body)`);
    err.status = r.status;
    throw err;
  }
  if (!r.ok) {
    const err = new Error(data.detail || `Enrolment failed (${r.status})`);
    err.status = r.status;
    err.data = data;
    throw err;
  }
  return data;
}

export async function fetchMyEnrollments(courseIds = []) {
  const qs = new URLSearchParams();
  if (courseIds.length) qs.set("course_ids", courseIds.join(","));
  const r = await fetch(`${API}/api/tutor/my-enrollments?${qs.toString()}`, {
    headers: { Accept: "application/json", ...authHeaders() },
  });
  if (!r.ok) return { course_ids: [] };
  const data = await safeReadJson(r);
  if (data?._empty || data?._raw) return { course_ids: [] };
  return data;
}
